import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  endorsementCatalog,
  type EndorsementSubtype,
  type EndorsementType,
  type Policy,
} from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';

import { caminhoDoAssistente } from './endosso';
import { SimplifiedEndorsementDialog } from './PolicyRequests';

/**
 * "Solicitacao de endosso" do OnPoint (END-018 a END-022), aberto pelo "Solicitar
 * endosso" da linha e do detalhe da apolice.
 *
 * A lista e a dos subtipos ativos dos tipos habilitados da empresa. Sem tipo
 * habilitado, o aviso "Atencao!" do OnPoint. "Iniciar" confere a elegibilidade da
 * apolice para o tipo no servidor e abre o assistente; a recusa vem com o motivo.
 *
 * A solicitacao simplificada pelo tipo de antes (valor, prazo, tomador e dados)
 * continua disponivel no rodape, porque o endosso de alteracao do assistente ainda
 * espera o calculo do P-END-2.
 */
export function EndorsementRequestDialog({
  policy,
  open,
  onClose,
}: {
  policy: Policy | null;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [subtipo, setSubtipo] = useState('');
  const [simplificada, setSimplificada] = useState(false);

  const tipos = useQuery({
    queryKey: ['endorsement-types', 'habilitados'],
    queryFn: ({ signal }) => endorsementCatalog.types(true, signal),
    enabled: open,
    retry: false,
  });
  const subtipos = useQuery({
    queryKey: ['endorsement-subtypes', 'ativos'],
    queryFn: ({ signal }) => endorsementCatalog.subtypes(undefined, false, signal),
    enabled: open && (tipos.data?.length ?? 0) > 0,
    retry: false,
  });

  const habilitados = tipos.data ?? [];
  const porTipo = new Map(habilitados.map((tipo) => [tipo.id, tipo]));
  const opcoes = (subtipos.data ?? [])
    .filter((item) => porTipo.has(item.type_id))
    .map((item) => ({
      value: item.id,
      label: `${porTipo.get(item.type_id)?.name ?? ''} - ${item.name}`,
    }));

  const iniciar = useMutation({
    mutationFn: async () => {
      if (!policy) throw new Error('Erro ao buscar apolice');
      const escolhido = (subtipos.data ?? []).find((item) => item.id === subtipo);
      const tipo = escolhido ? porTipo.get(escolhido.type_id) : undefined;
      if (!escolhido || !tipo)
        throw new Error('Tipo de endosso invalido ou nao implementado.');
      const elegivel = await endorsementCatalog.eligibility(policy.id, tipo.code);
      return { elegivel, escolhido, tipo };
    },
    onSuccess: ({
      elegivel,
      escolhido,
      tipo,
    }: {
      elegivel: Awaited<ReturnType<typeof endorsementCatalog.eligibility>>;
      escolhido: EndorsementSubtype;
      tipo: EndorsementType;
    }) => {
      if (!elegivel.eligible || !policy) return;
      const rascunho = elegivel.drafts.find(
        (item) => item.endorsement_subtype_id === escolhido.id,
      );
      onClose();
      void navigate(
        caminhoDoAssistente(policy.id, tipo.code, escolhido.id, rascunho?.id),
      );
    },
  });

  if (simplificada) {
    return (
      <SimplifiedEndorsementDialog policy={policy} open={open} onClose={onClose} />
    );
  }

  const semTipo = tipos.isSuccess && habilitados.length === 0;
  const recusa =
    iniciar.data && !iniciar.data.elegivel.eligible ? iniciar.data.elegivel : null;

  if (semTipo) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Atencao!"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSimplificada(true)}>
              Solicitacao simplificada
            </Button>
            <Button onClick={onClose}>Fechar</Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">
          Desculpe, mas nao e possivel solicitar um endosso pois nao existe nenhum tipo
          habilitado.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitacao de endosso"
      description="Informe o tipo de solicitacao para iniciar o processo de endosso."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => setSimplificada(true)}>
            Solicitacao simplificada
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={iniciar.isPending}
            disabled={subtipo === ''}
            onClick={() => iniciar.mutate()}
          >
            Iniciar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {tipos.isError ? (
          <Alert tone="danger" title="Erro ao carregar permissoes">
            {tipos.error.message}
          </Alert>
        ) : null}
        <Select
          label="Selecione o tipo de endosso"
          placeholder={
            subtipos.isPending && tipos.isSuccess ? 'Carregando motivos' : 'Selecione'
          }
          options={opcoes}
          value={subtipo}
          onChange={(event) => {
            setSubtipo(event.target.value);
            iniciar.reset();
          }}
        />
        {tipos.isSuccess && subtipos.isSuccess && opcoes.length === 0 ? (
          <Alert tone="warning" title="Nenhum subtipo ativo">
            Os tipos habilitados ainda nao tem subtipo configurado. A configuracao fica
            em Endossos, Configuracao.
          </Alert>
        ) : null}
        {iniciar.isError ? (
          <Alert tone="danger" title="Nao foi possivel iniciar o endosso">
            {iniciar.error.message}
          </Alert>
        ) : null}
        {recusa ? (
          <Alert tone="warning" title="A apolice nao aceita este endosso agora">
            <ul className="list-disc pl-4">
              {recusa.reasons.map((motivo) => (
                <li key={motivo}>{motivo}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
}
